import { queryOptions, useQuery } from '@tanstack/react-query';

import { K8sProvider } from '@cast/shared-types';
import { ClusterOnboardingPath } from '@cast/types';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

const EXTERNAL_CLUSTER_BOOTSTRAP_SCRIPT_QUERY_KEY =
  'external-cluster-bootstrap-script';

const getConnectAndEnableCastAiScriptQueryKey = (
  params: ConnectAndEnableCastAiScriptParams
) =>
  [
    EXTERNAL_CLUSTER_BOOTSTRAP_SCRIPT_QUERY_KEY,
    params.clusterName,
    params.clusterProvider,
    params.installGPUPlugin,
    params.installSecurityAgent,
    params.installAutoscalerAgent,
    params.installGpuMetricsExporter,
    params.installReliabilityMetrics,
    params.installNetflowExporter,
    params.installAiOptimizerProxy,
    params.installWorkloadAutoscaler,
    params.installOmni,
    params.installPodMutator,
    params.installSecurityImageScanning,
    params.installSecurityCompliance,
    params.onboardingPath,
  ] as const;

type ConnectAndEnableCastAiScriptParams = {
  clusterName?: string;
  clusterProvider: K8sProvider;
  /**
   * `install*` flags are forwarded as query parameters to the connect-script
   * endpoint. Pass `true`/`false` to opt in or out of a feature; pass
   * `undefined` (or omit the key) to leave the parameter off the request so
   * the server falls back to its default behaviour — used by the OneClick
   * "Connect a cluster" flow to render a bare `castctl cluster connect`
   * command (api-token / api-region / organization-id only) for orgs that
   * are not reporting-trial eligible and let castctl decide which features
   * to install.
   */
  installGPUPlugin?: boolean;
  installSecurityAgent?: boolean;
  installAutoscalerAgent?: boolean;
  installGpuMetricsExporter?: boolean;
  installReliabilityMetrics?: boolean;
  installNetflowExporter?: boolean;
  installAiOptimizerProxy?: boolean;
  installWorkloadAutoscaler?: boolean;
  installOmni?: boolean;
  installPodMutator?: boolean;
  installSecurityImageScanning?: boolean;
  installSecurityCompliance?: boolean;
  enabled?: boolean;
  onboardingPath?: ClusterOnboardingPath;
};

const getConnectAndEnableCastAiScriptQueryOptions = ({
  clusterName,
  clusterProvider,
  installGPUPlugin,
  installSecurityAgent,
  installAutoscalerAgent,
  installGpuMetricsExporter,
  installReliabilityMetrics,
  installNetflowExporter,
  installAiOptimizerProxy,
  installWorkloadAutoscaler,
  installOmni,
  installPodMutator,
  installSecurityImageScanning,
  installSecurityCompliance,
  onboardingPath,
  enabled = true,
}: ConnectAndEnableCastAiScriptParams) =>
  queryOptions({
    queryKey: getConnectAndEnableCastAiScriptQueryKey({
      clusterName,
      clusterProvider,
      installGPUPlugin,
      installSecurityAgent,
      installAutoscalerAgent,
      installGpuMetricsExporter,
      installReliabilityMetrics,
      installNetflowExporter,
      installAiOptimizerProxy,
      installWorkloadAutoscaler,
      installOmni,
      installPodMutator,
      installSecurityImageScanning,
      installSecurityCompliance,
      onboardingPath,
    }),
    queryFn: async () => {
      const { data } = await apiClient.clusters.getConnectAndEnableCastAiScript(
        {
          clusterName,
          provider: clusterProvider,
          nvidiaDevicePlugin: installGPUPlugin,
          installSecurityAgent,
          installAutoscalerAgent,
          installGpuMetricsExporter,
          installReliabilityMetrics,
          installNetflowExporter,
          installAiOptimizerProxy,
          installWorkloadAutoscaler,
          installOmni,
          installPodMutator,
          installSecurityImageScanning,
          installSecurityCompliance,
          onboardingPath,
        }
      );

      return data;
    },
    refetchOnWindowFocus: false,
    enabled: makeIsEnabled(enabled),
  });

export const useConnectAndEnableCastAiScriptQuery = ({
  clusterName,
  clusterProvider,
  installGPUPlugin,
  installSecurityAgent,
  installAutoscalerAgent,
  installGpuMetricsExporter,
  installReliabilityMetrics,
  installNetflowExporter,
  installAiOptimizerProxy,
  installWorkloadAutoscaler,
  installOmni,
  installPodMutator,
  installSecurityImageScanning,
  installSecurityCompliance,
  onboardingPath,
  enabled = true,
}: ConnectAndEnableCastAiScriptParams) => {
  const { isLoading, isFetching, data, error, refetch } = useQuery(
    getConnectAndEnableCastAiScriptQueryOptions({
      clusterName,
      clusterProvider,
      installGPUPlugin,
      installSecurityAgent,
      installAutoscalerAgent,
      installGpuMetricsExporter,
      installReliabilityMetrics,
      installNetflowExporter,
      installAiOptimizerProxy,
      installWorkloadAutoscaler,
      installOmni,
      installPodMutator,
      installSecurityImageScanning,
      installSecurityCompliance,
      onboardingPath,
      enabled,
    })
  );

  return {
    isLoading,
    isFetching,
    script: data?.script,
    error,
    refetch,
  };
};
